import { post, get, put } from 'aws-amplify/api';
import * as Sentry from '@sentry/react';
import { ApiInput, RestApiOptionsBase, RestApiResponse } from '@aws-amplify/api-rest/dist/esm/types';
import {
  sendApplicationError,
  sendSystemError,
  FINGERPRINT_APPLICATION_ERROR,
  FINGERPRINT_SYSTEM_ERROR,
  createCustomError
} from '@/lib/sentryClient';

/**
 * NOTE:
 * 以下処理は既存実装のエラーハンドリングに合わせてエラーが起きた場合に関数内で処理せずにエラーを呼び出し元に投げるようにしている。
 * 理想はエラーハンドリングは関数内で行い呼び出し元にはエラーを返さないようにしたい。
 * 理由としては関数内でカスタムするためにSentryにログを送信しているが、呼び出しもとにエラーを伝搬させることで
 * 呼び出し元でも他処理と相まってSentryにログを送る関係で2重になってしまうため。
 * なので現状は呼び出し元でError.nameがCUSTOM_ERROR_HANDLEの場合はSentryにログを送らないように制御している
 * 以下の型でレスポンスを返すイメージ
 * export type Customrror = {
    code: number;
    error: Error;
   };
   export type Result<T> = {
    data?: T;
    error?: CustomError;
};
*/

// RESTAPIのPOSTをラップする
export const executePost = async <T>(input: ApiInput<RestApiOptionsBase>): Promise<T> => {
  try {
    const operation = post(input);
    const response = await operation.response;
    const extra = {
      Rest: {
        method: 'POST',
        APIName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams),
        response: ''
      }
    };
    if (response.statusCode >= 400 && response.statusCode < 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_APPLICATION_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendApplicationError(error, input.apiName, extra);
      throw error;
    }
    if (response.statusCode >= 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendSystemError(error, input.apiName, extra);
      throw error;
    }
    const data: T = (await response.body.json()) as T;

    Sentry.addBreadcrumb({
      category: 'REST',
      message: `${input.apiName} executed`,
      data: {
        method: 'POST',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        queryParams: JSON.stringify(input.options?.queryParams),
        header: JSON.stringify(input.options?.headers),
        response: JSON.stringify(data)
      }
    });
    return data;
  } catch (e) {
    const error = createCustomError(new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}`));
    if (e instanceof Error) {
      error.message = `${error.message}: ${e.message}`;
      error.stack = e.stack;
    }
    error.name = `${input.apiName} ${error.name}`;
    const extra = {
      Rest: {
        method: 'POST',
        apiName: input.apiName,
        path: input.path,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    sendSystemError(error, input.apiName, extra);

    throw error;
  }
};

export const executePostAtAllResponse = async (input: ApiInput<RestApiOptionsBase>): Promise<RestApiResponse> => {
  try {
    const operation = post(input);
    const response = await operation.response;
    const extra = {
      Rest: {
        method: 'POST',
        APIName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams),
        response: ''
      }
    };
    if (response.statusCode >= 400 && response.statusCode < 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_APPLICATION_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendApplicationError(error, input.apiName, extra);
      return response;
    }
    if (response.statusCode >= 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendSystemError(error, input.apiName, extra);
      return response;
    }
    const data = await response.body.json();

    Sentry.addBreadcrumb({
      category: 'REST',
      message: `${input.apiName} executed`,
      data: {
        method: 'POST',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        queryParams: JSON.stringify(input.options?.queryParams),
        header: JSON.stringify(input.options?.headers),
        response: JSON.stringify(data)
      }
    });
    return response;
  } catch (e) {
    const error = createCustomError(new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}`));
    if (e instanceof Error) {
      error.message = `${error.message}: ${e.message}`;
    }
    error.name = `${input.apiName} ${error.name}`;
    const extra = {
      Rest: {
        method: 'POST',
        apiName: input.apiName,
        path: input.path,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    sendSystemError(error, input.apiName, extra);

    throw error;
  }
};

// RESTAPIのGETをラップする
export const executeGet = async <T>(input: ApiInput<RestApiOptionsBase>): Promise<T> => {
  try {
    const operation = get(input);
    const response = await operation.response;
    const extra = {
      Rest: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        response: '',
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    if (response.statusCode >= 400 && response.statusCode < 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendApplicationError(error, input.apiName, extra);
      throw error;
    }
    if (response.statusCode >= 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendSystemError(error, input.apiName, extra);
      throw error;
    }

    const data: T = (await response.body.json()) as T;

    Sentry.addBreadcrumb({
      category: 'REST',
      message: `${input.apiName} executed`,
      data: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        queryParams: JSON.stringify(input.options?.queryParams),
        header: JSON.stringify(input.options?.headers),
        response: JSON.stringify(data)
      }
    });

    return data;
  } catch (e) {
    const error = createCustomError(new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}`));
    if (e instanceof Error) {
      error.message = `${error.message}: ${e.message}`;
    }
    error.name = `${input.apiName} ${error.name}`;
    const extra = {
      Rest: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    sendSystemError(error, input.apiName, extra);
    throw error;
  }
};

export const executeGetAtAllResponse = async (input: ApiInput<RestApiOptionsBase>): Promise<RestApiResponse> => {
  try {
    const operation = get(input);
    const response = await operation.response;
    const extra = {
      Rest: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        response: '',
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    if (response.statusCode >= 400 && response.statusCode < 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendApplicationError(error, input.apiName, extra);
      throw response;
    }
    if (response.statusCode >= 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendSystemError(error, input.apiName, extra);
      return response;
    }

    const data = await response.body.json();

    Sentry.addBreadcrumb({
      category: 'REST',
      message: `${input.apiName} executed`,
      data: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        queryParams: JSON.stringify(input.options?.queryParams),
        header: JSON.stringify(input.options?.headers),
        response: JSON.stringify(data)
      }
    });

    return response;
  } catch (e) {
    const error = createCustomError(new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}`));
    if (e instanceof Error) {
      error.message = `${error.message}: ${e.message}`;
    }
    error.name = `${input.apiName} ${error.name}`;
    const extra = {
      Rest: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    sendSystemError(error, input.apiName, extra);
    throw error;
  }
};

// RESTAPIのPUTをラップする
export const executePut = async <T>(input: ApiInput<RestApiOptionsBase>): Promise<T> => {
  try {
    const operation = put(input);
    const response = await operation.response;
    const extra = {
      Rest: {
        method: 'PUT',
        path: input.path,
        apiName: input.apiName,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        response: '',
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    if (response.statusCode >= 400 && response.statusCode < 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendApplicationError(error, input.apiName, extra);
      throw error;
    }
    if (response.statusCode >= 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendSystemError(error, input.apiName, extra);
      throw error;
    }

    const data = (await response.body.json()) as T;

    Sentry.addBreadcrumb({
      category: 'REST',
      message: `${input.apiName} executed`,
      data: {
        method: 'PUT',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        queryParams: JSON.stringify(input.options?.queryParams),
        header: JSON.stringify(input.options?.headers),
        response: JSON.stringify(data)
      }
    });

    return data;
  } catch (e) {
    const error = createCustomError(new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}`));
    if (e instanceof Error) {
      error.message = `${error.message}: ${e.message}`;
    }
    error.name = `${input.apiName} ${error.name}`;
    const extra = {
      Rest: {
        method: 'PUT',
        apiName: input.apiName,
        path: input.path,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    sendSystemError(error, input.apiName, extra);
    throw error;
  }
};
